import type {BagStore} from "./BagStore";
import LoadingBarStore from "./LoadingBarStore.tsx";
import type AuthStore from "../authentication/AuthStore";
import {makeAutoObservable, runInAction} from "mobx";
import type {Bag} from "../services/classes/Bag";
import type {Location, NavigateFunction} from "react-router";
import type {DatabagRouteMatch, MithraHistoryState} from "../routing/catRoutes";
import {routes} from "../routing/routes";
import type {MithraMaterializedApi} from "../services/MithraMaterializedApi";
import type {Params} from "../routing/routeClasses";

export class BagLoadingStore {
    isInSync = true;
    bagNotFound = false;

    constructor(
        private api: MithraMaterializedApi,
        private bagStore: BagStore,
        private authStore: AuthStore,
    ) {
        makeAutoObservable(this)
    }

    loadBag(bagId: number): Promise<Bag> {
        LoadingBarStore.start(true);
        this.isInSync = false;
        return this.api.getBag(bagId)
            .then(r => {
                const bag = r.data;
                LoadingBarStore.setComplete();
                this.bagStore.setBag(bag);
                return bag;
            })
            .finally(() => runInAction(() => this.isInSync = true))
    }

    loadBagWithoutLoadingStores(bagId: number): Promise<Bag> {
        LoadingBarStore.start(true);
        this.isInSync = false;
        return this.api.getBag(bagId)
            .then(r => {
                const bag = r.data;
                LoadingBarStore.setComplete();
                this.bagStore.set(bag);
                return bag;
            })
            .finally(() => runInAction(() => this.isInSync = true))
    }

    /**
     * Initialize the bag store
     * @return Best estimated bag id (Beware; it might be invalid)
     */
    initBag(
        location: Location<MithraHistoryState>,
        navigate: NavigateFunction,
        params: Params<DatabagRouteMatch>,
    ): number | undefined | null {
        // TODO: performance gain can be achieved by limiting this call (i.e. deps = [])
        if (!this.isInSync) {
            // Bag is already being retrieved
            return undefined;
        }
        if (this.bagNotFound) {
            return null;
        }
        if (this.bagStore.bag) {
            // Bag is already present
            return this.bagStore.bagId;
        }
        if (location.state?.bag) {
            // TODO:
            // - How to check how much time has passed?
            // - Tokens might need to be refreshed
            // Attempt to interpret bag from Redirect
            const bag = location.state.bag;
            return bag.id;
        } else {
            // Attempt to interpret job from URL
            console.debug(`Load job ${params.id} from URL`);
            const bagId = Number(params.id);
            if (isNaN(bagId)) {
                this.bagNotFound = true;
                return null;
            }
            this.loadBag(bagId)
                .catch(err => {
                    const unAuth = this.authStore.authentication.catchApiError(err);
                    if (unAuth) {
                        navigate(routes.login);
                        return
                    }

                    // TODO what to do when bag cannot be found on the server anymore?
                    this.bagNotFound = true;
                });
            return bagId;
        }
    }

    initBagAsync(
        location: Location<MithraHistoryState>,
        navigate: NavigateFunction,
        params: Params<DatabagRouteMatch>,
    ): Promise<number | void> | number | undefined | null {
        // TODO: performance gain can be achieved by limiting this call (i.e. deps = [])
        if (!this.isInSync) {
            // Bag is already being retrieved
            return undefined;
        }
        if (this.bagNotFound) {
            return null;
        }
        if (this.bagStore.bag) {
            // Bag is already present
            return this.bagStore.bagId;
        }
        if (location.state?.bag) {
            // TODO:
            // - How to check how much time has passed?
            // - Tokens might need to be refreshed
            // Attempt to interpret bag from Redirect
            const bag = location.state.bag;
            return bag.id;
        } else {
            // Attempt to interpret job from URL
            const bagId = Number(params.id);
            if (isNaN(bagId)) {
                this.bagNotFound = true;
                return null;
            }
            return this.loadBag(bagId)
                .then(bag => bag.id)
                .catch(err => {
                    const unAuth = this.authStore.authentication.catchApiError(err);
                    if (unAuth) {
                        navigate(routes.login);
                        return
                    }

                    // TODO what to do when bag cannot be found on the server anymore?
                    this.bagNotFound = true;
                });
        }
    }
}
